import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, ResultCard } from "./pipe-helpers";
import { WEIGHT_PIPE_DATA, WATER_DENSITY_LB_PER_CUIN } from "./pipe-weight-data";

export const PipeWeightCalculator = () => {
  const [matIdx, setMatIdx] = useState("0");
  const [sizeIdx, setSizeIdx] = useState("5");
  const [length, setLength] = useState("");
  const [fillType, setFillType] = useState<"water" | "empty" | "glycol">("water");

  const mat = WEIGHT_PIPE_DATA[parseInt(matIdx)];
  const pipe = mat?.sizes[parseInt(sizeIdx)];
  const L = parseFloat(length);

  const fillDensity = fillType === "water" ? WATER_DENSITY_LB_PER_CUIN
    : fillType === "glycol" ? WATER_DENSITY_LB_PER_CUIN * 1.04
    : 0;

  const result = useMemo(() => {
    if (!pipe || !L || L <= 0) return null;
    const pipeWeight = pipe.wt * L;
    const idArea = Math.PI * (pipe.id / 2) ** 2;
    const fillVolume = idArea * L * 12;
    const fillWeight = fillVolume * fillDensity;
    const totalWeight = pipeWeight + fillWeight;
    const fillGallons = fillVolume / 231;
    return { pipeWeight, fillWeight, totalWeight, fillGallons };
  }, [pipe, L, fillDensity]);

  const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">{children}</label>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Material</FieldLabel>
          <Select value={matIdx} onValueChange={(v) => { setMatIdx(v); setSizeIdx("0"); }}>
            <SelectTrigger className="bg-card border-border font-mono-data text-xs sm:text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {WEIGHT_PIPE_DATA.map((m, i) => (
                <SelectItem key={i} value={String(i)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel>Pipe Size</FieldLabel>
          <Select value={sizeIdx} onValueChange={setSizeIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {mat?.sizes.map((s, i) => (
                <SelectItem key={i} value={String(i)}>{s.nom}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Length (ft)</FieldLabel>
          <Input type="number" inputMode="decimal" placeholder="Pipe length…" value={length} onChange={(e) => setLength(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div>
          <FieldLabel>Fill</FieldLabel>
          <Select value={fillType} onValueChange={(v) => setFillType(v as "water" | "empty" | "glycol")}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="water">Water</SelectItem>
              <SelectItem value="glycol">50% Glycol</SelectItem>
              <SelectItem value="empty">Empty</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {pipe && (
        <div className="trade-card">
          <div className="grid grid-cols-3 gap-y-1 text-[11px]">
            <span className="text-muted-foreground">OD</span>
            <span className="text-muted-foreground">ID</span>
            <span className="text-muted-foreground">Wt/ft</span>
            <span className="font-mono-data">{pipe.od}"</span>
            <span className="font-mono-data">{pipe.id}"</span>
            <span className="font-mono-data">{pipe.wt} lb</span>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Total Weight" value={fmt(result.totalWeight, 1)} unit="lbs" accent />
          <ResultCard label="Pipe Weight" value={fmt(result.pipeWeight, 1)} unit="lbs" />
          {fillType !== "empty" && (
            <>
              <ResultCard label={fillType === "glycol" ? "Glycol Fill Weight" : "Water Fill Weight"} value={fmt(result.fillWeight, 1)} unit="lbs" />
              <ResultCard label="Fill Volume" value={fmt(result.fillGallons, 1)} unit="gal" />
            </>
          )}
        </div>
      )}
    </div>
  );
};
